// src/services/fundamentalAnalysisService.ts
import { alphaVantageService } from './alphaVantage/alphaVantageService';
import { marketDataService } from './marketData/marketDataService';

export interface FinancialStatement {
  [key: string]: number | string;
}

export interface FundamentalData {
  overview: {
    Name?: string;
    Symbol?: string;
    Description?: string;
    Exchange?: string;
    Industry?: string;
    Sector?: string;
    MarketCapitalization?: string;
    EBITDA?: string;
    PERatio?: string;
    PEGRatio?: string;
    BookValue?: string;
    DividendPerShare?: string;
    DividendYield?: string;
    EPS?: string;
    RevenuePerShareTTM?: string;
    ProfitMargin?: string;
    QuarterlyEarningsGrowthYOY?: string;
    QuarterlyRevenueGrowthYOY?: string;
    AnalystTargetPrice?: string;
    TrailingPE?: string;
    ForwardPE?: string;
    PriceToSalesRatioTTM?: string;
    PriceToBookRatio?: string;
    EVToRevenue?: string;
    EVToEBITDA?: string;
    Beta?: string;
    WeekHigh52?: string;
    WeekLow52?: string;
    DayMovingAverage50?: string;
    DayMovingAverage200?: string;
    SharesOutstanding?: string;
    [key: string]: string | undefined;
  };
  incomeStatement: FinancialStatement[];
  balanceSheet: FinancialStatement[];
  cashFlow: FinancialStatement[];
}

// Format large numbers for display
const formatLargeNumber = (value: number | string): string => {
  if (typeof value === 'string') {
    value = parseFloat(value);
  }
  
  if (isNaN(value)) return 'N/A';
  
  if (value >= 1000000000) {
    return `${(value / 1000000000).toFixed(2)}B`;
  } else if (value >= 1000000) {
    return `${(value / 1000000).toFixed(2)}M`;
  } else if (value >= 1000) {
    return `${(value / 1000).toFixed(2)}K`;
  }
  
  return value.toFixed(2);
};

// Utility to calculate ratios and metrics from financial data
const calculateFinancialRatios = (incomeStatement: any, balanceSheet: any) => {
  const ratios: any = {};
  
  if (incomeStatement && incomeStatement.length > 0 && balanceSheet && balanceSheet.length > 0) {
    const income = incomeStatement[0];
    const balance = balanceSheet[0];
    
    // Calculate P/E ratio if we have net income
    if (income.netIncome && income.netIncome !== 0) {
      ratios.PERatio = (balance.marketCap || 0) / income.netIncome;
    }
    
    // Calculate Price to Book ratio
    if (balance.totalShareholderEquity && balance.totalShareholderEquity !== 0) {
      ratios.PriceToBookRatio = (balance.marketCap || 0) / balance.totalShareholderEquity;
    }
    
    // Calculate ROE
    if (balance.totalShareholderEquity && balance.totalShareholderEquity !== 0) {
      ratios.ROE = (income.netIncome || 0) / balance.totalShareholderEquity;
    }
    
    // Calculate Profit Margin
    if (income.revenue && income.revenue !== 0) {
      ratios.ProfitMargin = (income.netIncome || 0) / income.revenue;
    }
  }
  
  return ratios;
};

export const fundamentalAnalysisService = {
  /**
   * Get fundamental data for a symbol
   * @param symbol Stock symbol
   * @param period Period type (quarterly, annual, ttm)
   * @returns Fundamental data
   */
  getFundamentalData: async (symbol: string, period: string = 'quarterly'): Promise<FundamentalData> => {
    try {
      // First try to get data from Alpaca via marketDataService
      try {
        // Get quote data for current price and market cap
        const quoteData = await marketDataService.getQuote(symbol, 'alpaca');
        
        // Get historical prices to calculate metrics and trends
        const historicalData = await marketDataService.getTimeSeriesDaily(symbol, 'alpaca');
        
        if (quoteData && historicalData && historicalData.length > 0) {
          // Sort by date (newest first)
          historicalData.sort((a, b) => new Date(b.timestamp || b.t).getTime() - new Date(a.timestamp || a.t).getTime());
          
          // Extract recent prices to calculate metrics
          const currentPrice = quoteData.price;
          const outstandingShares = 1000000; // Placeholder - would need to get from proper API
          const marketCap = currentPrice * outstandingShares;
          
          // Calculate 52-week high/low
          const yearData = historicalData.filter(bar => 
            new Date(bar.timestamp || bar.t).getTime() > Date.now() - 365 * 24 * 60 * 60 * 1000
          );
          
          const high52 = Math.max(...yearData.map(bar => parseFloat(bar.high || bar.h)));
          const low52 = Math.min(...yearData.map(bar => parseFloat(bar.low || bar.l)));
          
          // Create simplified financial statements using price data
          // Note: In a real implementation, we would need an API for proper financial data
          const mockIncomeStatements = generateMockIncomeStatements(symbol, marketCap, period);
          const mockBalanceSheets = generateMockBalanceSheets(symbol, marketCap, period);
          const mockCashFlows = generateMockCashFlows(symbol, marketCap, period);
          
          // Calculate financial ratios
          const ratios = calculateFinancialRatios(mockIncomeStatements, mockBalanceSheets);
          
          // Create company overview using available data
          const overview = {
            Symbol: symbol,
            Name: `${symbol} Corporation`, // Would need proper company name from API
            MarketCapitalization: marketCap.toString(),
            PERatio: ratios.PERatio?.toString() || "N/A",
            PEGRatio: "N/A", // Would need growth estimates for proper PEG
            PriceToBookRatio: ratios.PriceToBookRatio?.toString() || "N/A",
            ProfitMargin: ratios.ProfitMargin?.toString() || "N/A",
            Beta: "N/A", // Would need proper beta calculation
            WeekHigh52: high52.toString(),
            WeekLow52: low52.toString(),
            // Other fields would be populated from a proper financial data API
          };
          
          return {
            overview,
            incomeStatement: mockIncomeStatements,
            balanceSheet: mockBalanceSheets,
            cashFlow: mockCashFlows
          };
        }
      } catch (alpacaError) {
        console.warn('Alpaca data fetch failed, falling back to Alpha Vantage:', alpacaError);
      }
      
      // Fall back to Alpha Vantage if Alpaca doesn't have the required data
      // Get company overview
      const overview = await alphaVantageService.getOverview(symbol);
      
      // Get financial statements
      const incomePromise = alphaVantageService.getIncomeStatement(symbol);
      const balanceSheetPromise = alphaVantageService.getBalanceSheet(symbol);
      const cashFlowPromise = alphaVantageService.getCashFlow(symbol);
      
      // Wait for all requests to complete
      const [incomeData, balanceSheetData, cashFlowData] = await Promise.all([
        incomePromise, balanceSheetPromise, cashFlowPromise
      ]);
      
      // Extract financial statements based on period
      let incomeStatements: FinancialStatement[] = [];
      let balanceSheets: FinancialStatement[] = [];
      let cashFlows: FinancialStatement[] = [];
      
      if (incomeData && incomeData.quarterlyReports && period === 'quarterly') {
        incomeStatements = incomeData.quarterlyReports.slice(0, 4).map(report => {
          return {
            date: report.fiscalDateEnding,
            revenue: parseFloat(report.totalRevenue),
            grossProfit: parseFloat(report.grossProfit),
            netIncome: parseFloat(report.netIncome),
            eps: parseFloat(report.reportedEPS || '0')
          };
        });
      } else if (incomeData && incomeData.annualReports && (period === 'annual' || period === 'ttm')) {
        incomeStatements = incomeData.annualReports.slice(0, 3).map(report => {
          return {
            date: report.fiscalDateEnding,
            revenue: parseFloat(report.totalRevenue),
            grossProfit: parseFloat(report.grossProfit),
            netIncome: parseFloat(report.netIncome),
            eps: parseFloat(report.reportedEPS || '0')
          };
        });
      }
      
      if (balanceSheetData && balanceSheetData.quarterlyReports && period === 'quarterly') {
        balanceSheets = balanceSheetData.quarterlyReports.slice(0, 4).map(report => {
          return {
            date: report.fiscalDateEnding,
            totalAssets: parseFloat(report.totalAssets),
            totalLiabilities: parseFloat(report.totalLiabilities),
            totalShareholderEquity: parseFloat(report.totalShareholderEquity),
            cashAndEquivalents: parseFloat(report.cashAndCashEquivalentsAtCarryingValue)
          };
        });
      } else if (balanceSheetData && balanceSheetData.annualReports && (period === 'annual' || period === 'ttm')) {
        balanceSheets = balanceSheetData.annualReports.slice(0, 3).map(report => {
          return {
            date: report.fiscalDateEnding,
            totalAssets: parseFloat(report.totalAssets),
            totalLiabilities: parseFloat(report.totalLiabilities),
            totalShareholderEquity: parseFloat(report.totalShareholderEquity),
            cashAndEquivalents: parseFloat(report.cashAndCashEquivalentsAtCarryingValue)
          };
        });
      }
      
      if (cashFlowData && cashFlowData.quarterlyReports && period === 'quarterly') {
        cashFlows = cashFlowData.quarterlyReports.slice(0, 4).map(report => {
          return {
            date: report.fiscalDateEnding,
            operatingCashFlow: parseFloat(report.operatingCashflow),
            capitalExpenditures: parseFloat(report.capitalExpenditures),
            cashflowFromInvestment: parseFloat(report.cashflowFromInvestment),
            cashflowFromFinancing: parseFloat(report.cashflowFromFinancing),
            dividendPayout: parseFloat(report.dividendPayout || '0')
          };
        });
      } else if (cashFlowData && cashFlowData.annualReports && (period === 'annual' || period === 'ttm')) {
        cashFlows = cashFlowData.annualReports.slice(0, 3).map(report => {
          return {
            date: report.fiscalDateEnding,
            operatingCashFlow: parseFloat(report.operatingCashflow),
            capitalExpenditures: parseFloat(report.capitalExpenditures),
            cashflowFromInvestment: parseFloat(report.cashflowFromInvestment),
            cashflowFromFinancing: parseFloat(report.cashflowFromFinancing),
            dividendPayout: parseFloat(report.dividendPayout || '0')
          };
        });
      }
      
      return {
        overview: overview || {},
        incomeStatement: incomeStatements,
        balanceSheet: balanceSheets,
        cashFlow: cashFlows
      };
    } catch (error) {
      console.error(`Error getting fundamental data for ${symbol}:`, error);
      return getMockFundamentalData(symbol, period);
    }
  },
  
  // Format financial data for display
  formatFinancialValue: (value: number | string): string => {
    return formatLargeNumber(value);
  }
};

/**
 * Generate mock income statements when real data is unavailable
 */
const generateMockIncomeStatements = (symbol: string, marketCap: number, period: string): FinancialStatement[] => {
  const revenue = marketCap * 0.2; // 20% of market cap is annual revenue
  const grossMargin = 0.6; // 60% gross margin
  const netMargin = 0.15; // 15% net margin
  
  const today = new Date();
  const getQuarterEndDate = (monthsAgo: number): string => {
    const date = new Date(today);
    date.setMonth(date.getMonth() - monthsAgo);
    return date.toISOString().split('T')[0];
  };
  
  // Generate dates based on period
  const dates = period === 'quarterly' 
    ? [getQuarterEndDate(0), getQuarterEndDate(3), getQuarterEndDate(6), getQuarterEndDate(9)]
    : [getQuarterEndDate(0), getQuarterEndDate(12), getQuarterEndDate(24)];
  
  // Generate statements with quarterly or annual data
  return dates.map((date, index) => {
    const quarterlyRevenue = period === 'quarterly' ? revenue / 4 : revenue;
    const growthFactor = Math.pow(1.10, -index); // 10% YoY growth
    
    return {
      date,
      revenue: quarterlyRevenue * growthFactor,
      grossProfit: quarterlyRevenue * grossMargin * growthFactor,
      netIncome: quarterlyRevenue * netMargin * growthFactor,
      eps: (quarterlyRevenue * netMargin * growthFactor) / (marketCap / 100) // Simple EPS calculation
    };
  });
};

/**
 * Generate mock balance sheets when real data is unavailable
 */
const generateMockBalanceSheets = (symbol: string, marketCap: number, period: string): FinancialStatement[] => {
  const totalAssets = marketCap * 0.8; // 80% of market cap
  const liabilitiesRatio = 0.4; // 40% of assets are liabilities
  const cashRatio = 0.15; // 15% of assets are cash
  
  const today = new Date();
  const getQuarterEndDate = (monthsAgo: number): string => {
    const date = new Date(today);
    date.setMonth(date.getMonth() - monthsAgo);
    return date.toISOString().split('T')[0];
  };
  
  // Generate dates based on period
  const dates = period === 'quarterly' 
    ? [getQuarterEndDate(0), getQuarterEndDate(3), getQuarterEndDate(6), getQuarterEndDate(9)]
    : [getQuarterEndDate(0), getQuarterEndDate(12), getQuarterEndDate(24)];
  
  // Generate balance sheets
  return dates.map((date, index) => {
    const growthFactor = Math.pow(1.05, -index); // 5% YoY growth
    const assets = totalAssets * growthFactor;
    const liabilities = assets * liabilitiesRatio;
    
    return {
      date,
      marketCap, // Include market cap for ratio calculations
      totalAssets: assets,
      totalLiabilities: liabilities,
      totalShareholderEquity: assets - liabilities,
      cashAndEquivalents: assets * cashRatio
    };
  });
};

/**
 * Generate mock cash flow statements when real data is unavailable
 */
const generateMockCashFlows = (symbol: string, marketCap: number, period: string): FinancialStatement[] => {
  const revenue = marketCap * 0.2; // 20% of market cap is annual revenue
  const operatingMargin = 0.20; // 20% operating cash flow margin
  const capexRatio = 0.3; // 30% of operating cash flow goes to capex
  
  const today = new Date();
  const getQuarterEndDate = (monthsAgo: number): string => {
    const date = new Date(today);
    date.setMonth(date.getMonth() - monthsAgo);
    return date.toISOString().split('T')[0];
  };
  
  // Generate dates based on period
  const dates = period === 'quarterly' 
    ? [getQuarterEndDate(0), getQuarterEndDate(3), getQuarterEndDate(6), getQuarterEndDate(9)]
    : [getQuarterEndDate(0), getQuarterEndDate(12), getQuarterEndDate(24)];
  
  // Generate cash flow statements
  return dates.map((date, index) => {
    const quarterlyRevenue = period === 'quarterly' ? revenue / 4 : revenue;
    const growthFactor = Math.pow(1.10, -index); // 10% YoY growth
    const operatingCashFlow = quarterlyRevenue * operatingMargin * growthFactor;
    
    return {
      date,
      operatingCashFlow,
      capitalExpenditures: -operatingCashFlow * capexRatio,
      cashflowFromInvestment: -operatingCashFlow * capexRatio * 1.2, // Slightly more than capex
      cashflowFromFinancing: -operatingCashFlow * 0.2, // Some dividend/buyback activity
      dividendPayout: operatingCashFlow * 0.1 // 10% of OCF goes to dividends
    };
  });
};

/**
 * Get mock fundamental data when real data is unavailable
 */
const getMockFundamentalData = (symbol: string, period: string): FundamentalData => {
  // Generate realistic mock data
  const marketCap = 100000000000 + Math.random() * 900000000000; // $100B - $1T
  
  return {
    overview: {
      Symbol: symbol,
      Name: `${symbol} Corporation`,
      Exchange: 'NYSE',
      Industry: 'Technology',
      Sector: 'Information Technology',
      MarketCapitalization: marketCap.toString(),
      EBITDA: (marketCap * 0.1).toString(),
      PERatio: (20 + Math.random() * 15).toString(),
      PEGRatio: (1 + Math.random() * 2).toString(),
      BookValue: (marketCap * 0.3 / 1000000000).toString(),
      DividendPerShare: (1 + Math.random() * 3).toString(),
      DividendYield: (1 + Math.random() * 3).toString(),
      EPS: (5 + Math.random() * 10).toString(),
      RevenuePerShareTTM: (50 + Math.random() * 100).toString(),
      ProfitMargin: (10 + Math.random() * 20).toString(),
      AnalystTargetPrice: (100 + Math.random() * 300).toString(),
      Beta: (0.8 + Math.random() * 0.8).toString()
    },
    incomeStatement: generateMockIncomeStatements(symbol, marketCap, period),
    balanceSheet: generateMockBalanceSheets(symbol, marketCap, period),
    cashFlow: generateMockCashFlows(symbol, marketCap, period)
  };
};